// =====================================================================
//  retirement.js — PPA/PRS-style Retirement Accumulation calculator math
//
//  Pure, DOM-free formulas for the "Accumulation Plan" calculator
//  ("How Much Do You Need to Save for Retirement?"). Reverse-engineered
//  from the PPA PRS calculator and kept byte-faithful to its numbers so
//  our results match the reference tool.
//
//  Malaysian retirement conventions:
//    - EPF balance + statutory contributions compound at the EPF
//      dividend rate (default 5% p.a.).
//    - Retirement "target pot" is the present value (at retirement) of
//      the desired retirement spending, discounted at a small real
//      return (1% p.a.), then inflated from today's RM to future RM by
//      CPI over the years-to-retirement.
//    - The calculator solves for the annual RETURN the non-EPF money
//      (savings + personal PRS/investment contributions) must earn to
//      hit the target — EPF is treated as fixed, counted toward the goal.
//
//  This module is deliberately self-contained (no dependency on
//  Calc.finance) so it can later be lifted straight into a public API
//  that serves the calculation. It holds pure math and numeric constants
//  ONLY — no user-facing copy (that lives in the page template).
//
//  Public API:
//    Calc.retirement.compute(inputs, assumptions) -> full result object
//    Calc.retirement.defaults / .assumptions
//    plus each individual formula (targetNetAsset, requiredReturn, ...)
//    Run Calc.retirement._verify() in the console for a sanity check.
// =====================================================================
window.Calc = window.Calc || {};

Calc.retirement = {

  // ===== DEFAULTS ========================================================
  // Sensible Malaysian starting values so the calculator shows a
  // meaningful result on first paint (the reference tool starts blank
  // and forces data entry; a live calculator is friendlier pre-filled).
  defaults: {
    age_now: 30,
    age_retire: 60,
    age_end: 85,
    spend_month_today: 3000,            // RM/month in TODAY's value
    net_now_ex_epf: 20000,              // investable savings, excluding EPF
    epf_total_now: 50000,               // current EPF balance
    salary_gross_month: 5000,           // gross monthly salary (before EPF)
    epf_employee_pct: 11,               // statutory employee share
    epf_employer_pct: 13,               // statutory employer share
    salary_growth_apy: 3,               // % annual salary increase
    personal_contrib_pct: 0,            // voluntary % of salary invested outside EPF
    annual_topup: { amount: 0, month: 12 },
    // Reserved (no UI in the accumulation flow, kept for parity/API shape):
    lump_sum_before_retire: { amount: 0, at_age: null },
    // Risk questionnaire — timeBand is derived from years-to-retire.
    timeBand: null,                     // 'gt15' | '8to15' | 'lt8'
    swingView: 'some',                  // 'steady' | 'some' | 'big'
    maxDrop: '10',                      // '5' | '10' | '20'
  },

  // Advanced assumptions (CPI + EPF dividend are user-adjustable sliders;
  // real_return_for_pot is fixed, used only to discount the annuity).
  assumptions: {
    cpi: 0.03,                          // 3.0% inflation p.a.
    epf_nominal: 0.05,                  // 5.0% EPF dividend p.a.
    real_return_for_pot: 0.01,          // 1.0% real growth during retirement
  },

  // Max realistically-achievable return per recommended portfolio.
  FUND_MAX_RETURN: { Conservative: 0.10, Balanced: 0.15, Growth: 0.20 },

  // NOTE: portfolio descriptions and all other user-facing copy live in the
  // page template (page-calculator-retirement.php), never in JS — these are
  // SEO pages, so text must be server-rendered. When the public API is built,
  // its field/label definitions will be sourced server-side, not from here.

  // ===== SMALL HELPERS ===================================================
  _span(a, b) { return Math.max(0, b - a); },   // yA / Q1 in the source

  // ===== CORE FORMULAS ===================================================

  // Target Net Asset (retirement pot), in FUTURE RM at retirement. (Wu)
  //   annualSpendToday = monthly × 12
  //   pvAnnuity = PV of `yearsInRetire` years of spending discounted at
  //               the real return (money keeps growing during retirement)
  //   target = pvAnnuity × (1 + cpi)^yearsToRetire   (today's RM → future RM)
  targetNetAsset(spendMonthToday, ageNow, ageRetire, ageEnd, cpi, realReturn) {
    const yearsToRetire = this._span(ageNow, ageRetire);
    const yearsInRetire = this._span(ageRetire, ageEnd);
    const annualSpend = (spendMonthToday || 0) * 12;
    const h = realReturn;
    const pvAnnuity = h > 0
      ? (annualSpend * (1 - Math.pow(1 + h, -yearsInRetire))) / h
      : annualSpend * yearsInRetire;
    return pvAnnuity * Math.pow(1 + cpi, yearsToRetire);
  },

  // FV of the current EPF balance, grown at the EPF dividend rate. (gA)
  fvCurrentEpf(epfNow, ageNow, ageRetire, epfNominal) {
    if (!epfNow || epfNow <= 0) return 0;
    return epfNow * Math.pow(1 + epfNominal, this._span(ageNow, ageRetire));
  },

  // FV of future EPF contributions. Annual contribution =
  // (employee% + employer%) of gross annual salary; salary grows yearly;
  // balance compounds at the EPF dividend rate. (xA)
  fvEpfContributions(salaryMonth, empPct, emprPct, salaryGrowth, epfNominal, ageNow, ageRetire) {
    const years = this._span(ageNow, ageRetire);
    let salary = salaryMonth || 0, acc = 0;
    for (let i = 0; i < years; i++) {
      const annualContrib = salary * 12 * ((empPct + emprPct) / 100);
      acc = (acc + annualContrib) * (1 + epfNominal);
      salary = salary * (1 + salaryGrowth);
    }
    return acc;
  },

  // FV of personal contributions at a given return. Annual contribution =
  // personal% of gross annual salary + fixed annual top-up. (S0)
  // `returnRate` is the unknown solved for by requiredReturn().
  fvPersonalContributions(salaryMonth, personalPct, salaryGrowth, returnRate, ageNow, ageRetire, annualTopup) {
    const years = this._span(ageNow, ageRetire);
    let salary = salaryMonth || 0, acc = 0;
    for (let i = 0; i < years; i++) {
      const annualContrib = salary * 12 * (personalPct / 100);
      acc = (acc + annualContrib + (annualTopup || 0)) * (1 + returnRate);
      salary = salary * (1 + salaryGrowth);
    }
    return acc;
  },

  // Monthly salary grown to retirement. (gd)
  salaryAtRetirement(salaryMonth, salaryGrowthApy, ageNow, ageRetire) {
    return (salaryMonth || 0) * Math.pow(1 + salaryGrowthApy / 100, this._span(ageNow, ageRetire));
  },

  // ★ Required annual return on the PERSONAL (non-EPF) money so that
  //   fvNet + fvPersonal + fvLump + epfTotal = target. (hd)
  //   EPF is fixed at epf_nominal and counted toward the target.
  //   Returns 0 when already on track at a 0% personal return.
  requiredReturn(inputs, a) {
    const yrs = this._span(inputs.age_now, inputs.age_retire);
    const target = this.targetNetAsset(inputs.spend_month_today, inputs.age_now, inputs.age_retire, inputs.age_end, a.cpi, a.real_return_for_pot);
    const epfNow = this.fvCurrentEpf(inputs.epf_total_now, inputs.age_now, inputs.age_retire, a.epf_nominal);
    const epfCont = this.fvEpfContributions(inputs.salary_gross_month, inputs.epf_employee_pct, inputs.epf_employer_pct, inputs.salary_growth_apy / 100, a.epf_nominal, inputs.age_now, inputs.age_retire);
    const epfTotal = epfNow + epfCont;
    const topup = (inputs.annual_topup && inputs.annual_topup.amount) || 0;
    const lump = inputs.lump_sum_before_retire || { amount: 0, at_age: null };
    const persPrincipal = this.fvPersonalContributions(inputs.salary_gross_month, inputs.personal_contrib_pct, inputs.salary_growth_apy / 100, 0, inputs.age_now, inputs.age_retire, topup);
    const lumpNow = (lump.at_age && lump.at_age < inputs.age_retire) ? lump.amount : 0;

    if ((inputs.net_now_ex_epf || 0) + persPrincipal + lumpNow + epfTotal >= target) return 0;

    let lo = 0, hi = 0.40;
    const tol = 1e-4;
    for (let i = 0; i < 80; i++) {
      const b = (lo + hi) / 2;
      const fvNet = (inputs.net_now_ex_epf || 0) * Math.pow(1 + b, yrs);
      const fvPers = this.fvPersonalContributions(inputs.salary_gross_month, inputs.personal_contrib_pct, inputs.salary_growth_apy / 100, b, inputs.age_now, inputs.age_retire, topup);
      const fvLump = (lump.at_age && lump.at_age < inputs.age_retire)
        ? lump.amount * Math.pow(1 + b, inputs.age_retire - lump.at_age) : 0;
      if (fvNet + fvPers + fvLump + epfTotal >= target) hi = b; else lo = b;
      if (hi - lo < tol) return b;
    }
    return hi;
  },

  // Risk questionnaire score (0-100). (md)
  riskScore(inputs) {
    let s = 0;
    s += inputs.timeBand === 'gt15' ? 40 : inputs.timeBand === '8to15' ? 25 : 10;
    s += inputs.swingView === 'steady' ? 0 : inputs.swingView === 'some' ? 20 : 40;
    s += inputs.maxDrop === '5' ? 0 : inputs.maxDrop === '10' ? 15 : 30;
    return Math.min(100, s);
  },

  // Recommended portfolio from the risk score. (vd)
  fundTypeFromScore(score) {
    return score < 35 ? 'Conservative' : score < 65 ? 'Balanced' : 'Growth';
  },

  // Auto time band from years to retirement.
  timeBand(ageNow, ageRetire) {
    const yrs = this._span(ageNow, ageRetire);
    return yrs > 15 ? 'gt15' : yrs >= 8 ? '8to15' : 'lt8';
  },

  maxReturnForFund(fundType) {
    return this.FUND_MAX_RETURN[fundType] != null ? this.FUND_MAX_RETURN[fundType] : 0.15;
  },

  // EPF-vs-personal breakdown at retirement, split principal / growth. (xd)
  // Personal grows at `requiredReturn`; EPF at epf_nominal.
  breakdown(inputs, a, requiredReturn) {
    const years = this._span(inputs.age_now, inputs.age_retire);
    let persBal = inputs.net_now_ex_epf || 0, epfBal = inputs.epf_total_now || 0;
    let persPrin = inputs.net_now_ex_epf || 0, epfPrin = inputs.epf_total_now || 0;
    let salary = inputs.salary_gross_month || 0, lumpDone = false;
    const lump = inputs.lump_sum_before_retire || { amount: 0, at_age: null };
    const topup = (inputs.annual_topup && inputs.annual_topup.amount) || 0;
    for (let y = 0; y < years; y++) {
      const age = inputs.age_now + y;
      const persContrib = salary * 12 * ((inputs.personal_contrib_pct || 0) / 100);
      persBal = (persBal + persContrib + topup) * (1 + requiredReturn);
      persPrin += persContrib + topup;
      if (!lumpDone && lump.amount && lump.at_age && age >= lump.at_age && age < inputs.age_retire) {
        persBal += lump.amount; persPrin += lump.amount; lumpDone = true;
      }
      const epfContrib = salary * 12 * ((inputs.epf_employee_pct + inputs.epf_employer_pct) / 100);
      epfBal = (epfBal + epfContrib) * (1 + a.epf_nominal);
      epfPrin += epfContrib;
      salary = salary * (1 + inputs.salary_growth_apy / 100);
    }
    return {
      epf: { principal: epfPrin, interest: Math.max(0, epfBal - epfPrin), total: epfBal },
      personal: { principal: persPrin, interest: Math.max(0, persBal - persPrin), total: persBal },
    };
  },

  // Year-by-year series for the projection chart. (wd)
  // Each row: { year, age, epf, personal, remaining (balance to target), have }.
  onTrackSeries(inputs, a, requiredReturn, target) {
    const ageNow = inputs.age_now, ageRetire = inputs.age_retire;
    const years = this._span(ageNow, ageRetire);
    let salary = inputs.salary_gross_month || 0;
    let persBal = inputs.net_now_ex_epf || 0, epfBal = inputs.epf_total_now || 0;
    const rows = [];
    const have0 = epfBal + persBal;
    const topup = (inputs.annual_topup && inputs.annual_topup.amount) || 0;
    rows.push({ year: 0, age: Math.round(ageNow), epf: Math.max(0, epfBal), personal: Math.max(0, persBal), remaining: Math.max(0, target - have0), have: have0 });
    for (let x = 1; x <= years; x++) {
      const persContrib = salary * 12 * ((inputs.personal_contrib_pct || 0) / 100);
      persBal = (persBal + persContrib + topup) * (1 + requiredReturn);
      const epfContrib = salary * 12 * ((inputs.epf_employee_pct + inputs.epf_employer_pct) / 100);
      epfBal = (epfBal + epfContrib) * (1 + a.epf_nominal);
      salary = salary * (1 + inputs.salary_growth_apy / 100);
      const have = epfBal + persBal;
      rows.push({ year: x, age: Math.round(ageNow + x), epf: Math.max(0, epfBal), personal: Math.max(0, persBal), remaining: Math.max(0, target - have), have });
    }
    return rows;
  },

  // First age at which projected balance reaches the target (or null). (bA)
  ageTargetMet(series, target) {
    for (const row of series) if (row.have >= target) return row.age;
    return null;
  },

  // Detailed year-by-year table rows. (bd)
  detailedSeries(inputs, a, requiredReturn) {
    const ageNow = inputs.age_now, ageRetire = inputs.age_retire;
    const years = Math.max(0, Math.ceil(ageRetire - ageNow));
    let salary = inputs.salary_gross_month || 0;
    let persBal = inputs.net_now_ex_epf || 0, epfBal = inputs.epf_total_now || 0;
    const rows = [];
    let lumpDone = false;
    const lump = inputs.lump_sum_before_retire || { amount: 0, at_age: null };
    const topup = (inputs.annual_topup && inputs.annual_topup.amount) || 0;
    for (let m = 0; m <= years; m++) {
      const epfBeginning = epfBal, persBeginning = persBal, age = ageNow + m;
      if (m === 0) {
        rows.push({ year: 0, age: Math.round(age), epfBeginning, epfContributions: 0, epfGrowth: 0, epfEnding: epfBal, personalBeginning: persBeginning, personalContributions: 0, personalGrowth: 0, personalEnding: persBal, totalEnding: epfBal + persBal });
        continue;
      }
      const persContrib0 = salary * 12 * ((inputs.personal_contrib_pct || 0) / 100);
      let persContrib = persContrib0 + topup;
      if (!lumpDone && lump.amount && lump.at_age && age >= lump.at_age && age < ageRetire) {
        persContrib += lump.amount; lumpDone = true;
      }
      const persBase = persBeginning + persContrib;
      persBal = persBase * (1 + requiredReturn);
      const persGrowth = persBal - persBase;
      const epfContrib = salary * 12 * ((inputs.epf_employee_pct + inputs.epf_employer_pct) / 100);
      const epfBase = epfBeginning + epfContrib;
      epfBal = epfBase * (1 + a.epf_nominal);
      const epfGrowth = epfBal - epfBase;
      salary = salary * (1 + inputs.salary_growth_apy / 100);
      rows.push({ year: m, age: Math.round(age), epfBeginning, epfContributions: epfContrib, epfGrowth, epfEnding: epfBal, personalBeginning: persBeginning, personalContributions: persContrib, personalGrowth: persGrowth, personalEnding: persBal, totalEnding: epfBal + persBal });
    }
    return rows;
  },

  // ===== ONE-SHOT COMPUTE ================================================
  // Runs the full pipeline. `inputs` is merged over defaults, `assumptions`
  // over the default assumptions. Returns everything the UI (and a future
  // API) needs. timeBand is auto-derived from the ages if not supplied.
  compute(inputs = {}, assumptions = {}) {
    const i = Object.assign({}, this.defaults, inputs);
    // Keep nested objects from being clobbered by a shallow merge.
    i.annual_topup = Object.assign({}, this.defaults.annual_topup, inputs.annual_topup);
    i.lump_sum_before_retire = Object.assign({}, this.defaults.lump_sum_before_retire, inputs.lump_sum_before_retire);
    if (!i.timeBand) i.timeBand = this.timeBand(i.age_now, i.age_retire);
    const a = Object.assign({}, this.assumptions, assumptions);

    const target = this.targetNetAsset(i.spend_month_today, i.age_now, i.age_retire, i.age_end, a.cpi, a.real_return_for_pot);
    const requiredReturn = this.requiredReturn(i, a);
    const salaryAtRet = this.salaryAtRetirement(i.salary_gross_month, i.salary_growth_apy, i.age_now, i.age_retire);
    const breakdown = this.breakdown(i, a, requiredReturn);
    const projectedHave = breakdown.epf.total + breakdown.personal.total;
    const series = this.onTrackSeries(i, a, requiredReturn, target);
    const detailed = this.detailedSeries(i, a, requiredReturn);
    const score = this.riskScore(i);
    const fundType = this.fundTypeFromScore(score);
    const maxReturn = this.maxReturnForFund(fundType);
    const ageMet = this.ageTargetMet(series, target);

    return {
      inputs: i, assumptions: a,
      target, requiredReturn, salaryAtRet, breakdown, projectedHave,
      series, detailed, riskScore: score, fundType, maxReturn, ageTargetMet: ageMet,
      onTrack: requiredReturn <= 5e-4,               // effectively already on track
      notAchievable: requiredReturn > maxReturn,     // needs more than portfolio can deliver
      surplusBeforeRetire: ageMet !== null && i.age_retire > 0 && ageMet < i.age_retire,
    };
  },

  // ===== SANITY CHECK ====================================================
  _verify() {
    const tests = [];
    const eq = (a, b, tol, label) => {
      const pass = Math.abs(a - b) <= tol;
      tests.push({ label, pass, expected: b, actual: a });
      console[pass ? 'log' : 'error'](`${pass ? '✓' : '✗'} ${label} — expected ${b}, got ${a}`);
    };

    // Target pot: RM3,000/mo, 30→60→85, cpi 3%, real 1%
    const t = Calc.retirement.targetNetAsset(3000, 30, 60, 85, 0.03, 0.01);
    // annualSpend 36000; pv = 36000 * (1-1.01^-25)/0.01; inflate ×1.03^30
    eq(Math.round(t), 1924415, 2, 'targetNetAsset(3000,30,60,85,3%,1%)');

    // FV current EPF: 50000 @5% for 30y = 50000*4.32194 = 216097
    eq(Math.round(Calc.retirement.fvCurrentEpf(50000, 30, 60, 0.05)), 216097, 2, 'fvCurrentEpf');

    // requiredReturn should land in [0,0.40] and be finite
    const r = Calc.retirement.compute();
    eq(r.requiredReturn >= 0 && r.requiredReturn <= 0.40 && isFinite(r.requiredReturn) ? 1 : 0, 1, 0, 'requiredReturn in range');
    eq(r.series.length, 31, 0, 'series has years+1 rows (30y => 31)');
    eq(r.fundType === 'Conservative' || r.fundType === 'Balanced' || r.fundType === 'Growth' ? 1 : 0, 1, 0, 'fundType valid');

    const passed = tests.filter(x => x.pass).length;
    console.log(`\n=== retirement: ${passed}/${tests.length} passed ===`);
    return tests;
  },
};
